import React from 'react';
import { motion } from 'framer-motion';
import { getDayDiff } from '../utils/dateUtils';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { getThemeStyle } from '../utils/themeUtils';
import SafeIcon from '../common/SafeIcon';

export default function CuteCountdownCard({ item, onClick, onShare }) {
  const { isDark } = useTheme();
  const { t } = useLocale();
  const { diffDays, status, weekDayIndex } = getDayDiff(new Date(), item.targetDateISO);
  const currentStyle = getThemeStyle(item.theme, status, isDark);

  let label = t('common.remaining');
  if (status === 'today') label = t('common.justToday');
  if (status === 'elapsed') label = t('common.passed');

  const handleShare = (e) => {
    e.stopPropagation();
    onShare(item);
  };

  return (
    <motion.div
      whileHover={{ y: -5, scale: 1.02 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => onClick(item)}
      className={`relative cursor-pointer rounded-[2rem] p-6 shadow-sm hover:shadow-xl transition-all duration-300 bg-gradient-to-br ${currentStyle.bg} border-4 ${currentStyle.border} ${status === 'elapsed' && !isDark ? 'opacity-80 grayscale-[0.2] hover:opacity-100 hover:grayscale-0' : ''}`}
    >
      <button 
        onClick={handleShare}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/30 dark:bg-black/20 text-gray-700 dark:text-gray-200 hover:bg-white/60 dark:hover:bg-black/40 backdrop-blur-sm transition-all z-20"
      >
        <SafeIcon name="Share2" className="w-4 h-4" />
      </button>

      {status === 'elapsed' && (
        <div className="absolute top-4 right-14 text-4xl opacity-10 rotate-12 pointer-events-none">🕰️</div>
      )}

      <div className="flex justify-between items-start mb-4 relative z-10 pr-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 shrink-0 bg-white dark:bg-slate-800 rounded-2xl flex items-center justify-center text-2xl shadow-sm">
            {item.icon}
          </div>
          <div>
            {/* 放寬到 line-clamp-2，讓長標題有空間換行不被切斷 */}
            <h3 className={`font-bold text-lg line-clamp-2 break-words leading-tight mb-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {item.isDemo && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-white/60 dark:bg-black/30 backdrop-blur-sm border border-white/50 dark:border-white/10 text-pink-600 dark:text-pink-400 text-[10px] font-black mr-1.5 align-text-bottom shadow-sm">
                  ✨ {t('common.demoBadge')}
                </span>
              )}
              {t(item.title)}
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {item.targetDateISO} {t(`date.weekdays.${weekDayIndex}`)}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-end justify-between relative z-10">
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${currentStyle.badge}`}>
          {label}
        </span>
        {status !== 'today' && (
          <div className={`flex items-baseline gap-1 ${currentStyle.text}`}>
            <span className="text-5xl font-black drop-shadow-sm">{diffDays}</span>
            <span className="text-lg font-bold">{t('common.days')}</span>
          </div>
        )}
      </div>

      {item.note && (
        <div className="mt-4 text-sm bg-white/40 dark:bg-black/20 rounded-xl p-2 text-gray-700 dark:text-gray-300 line-clamp-1 backdrop-blur-sm font-medium">
          💡 {t(item.note)}
        </div>
      )}
    </motion.div>
  );
}